import { CircleQuestionMark, LogIn, Smartphone } from 'lucide-react';
import { useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { looksLikeEmail } from '../../../shared/email';
import { login } from '../../api/auth';
import { InstallGuide } from '../../components/InstallGuide';
import { Button, Card, ErrorBox, Sheet, TextInput } from '../../components/ui';
import { useT } from '../../i18n';
import { isStandalone } from '../../lib/standalone';
import { PrivacyLink } from '../privacy/PrivacyLink';
import { AuthBrand, AuthLayout, OriginalLogo } from './AuthLayout';
import { apiErrorText } from './errors';
import { PasswordInput } from './PasswordInput';

/** Sign-in (invite-only accounts; self-service reset comes later). Rendered outside the router. */
export default function LoginScreen() {
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const pwRef = useRef<HTMLInputElement>(null);

  /**
   * The keyboard's "next" key is a plain Enter, which would submit the form and complain about
   * the empty password. Move to the password field instead (unless it was already autofilled).
   */
  const emailKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || e.nativeEvent.isComposing || password) return;
    e.preventDefault();
    pwRef.current?.focus();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const em = email.trim();
    if (!em || !password) {
      setError(t('auth.login.missing'));
      (em ? pwRef : emailRef).current?.focus();
      return;
    }
    if (!looksLikeEmail(em)) {
      setError(t('auth.login.bad_email'));
      emailRef.current?.focus();
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await login(em, password);
      // AuthProvider now has the user; App switches to the app (or onboarding).
    } catch (err) {
      setError(apiErrorText(t, err));
      setPassword('');
      setBusy(false);
      pwRef.current?.focus();
    }
  };

  return (
    <AuthLayout footer={<OriginalLogo />}>
      <Card className="px-5 py-7 md:px-8 md:py-9">
        <AuthBrand />
        <h1 className="mt-6 mb-5 text-center text-2xl font-bold tracking-tight">{t('auth.login.title')}</h1>
        <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate method="post">
          <TextInput
            ref={emailRef}
            label={t('auth.login.email')}
            type="email"
            name="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="next"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={emailKeyDown}
            readOnly={busy}
            required
          />
          <PasswordInput
            ref={pwRef}
            label={t('auth.login.password')}
            name="password"
            autoComplete="current-password"
            enterKeyHint="go"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            readOnly={busy}
            required
          />
          {error ? <ErrorBox>{error}</ErrorBox> : null}
          <Button type="submit" size="lg" block icon={LogIn} busy={busy} className="mt-1">
            {busy ? t('auth.login.busy') : t('auth.login.submit')}
          </Button>
          {busy ? (
            <p className="text-center text-[15px] text-[var(--muted)]" aria-live="polite">
              {t('auth.login.slow')}
            </p>
          ) : null}
        </form>

        <div className="mt-6 flex items-start gap-3 rounded-xl bg-[var(--surface-2)] px-4 py-3">
          <CircleQuestionMark className="mt-0.5 size-5 shrink-0 text-[var(--accent)]" aria-hidden />
          <p className="text-[16px] text-[var(--ink-2)]">
            <strong className="font-semibold text-[var(--ink)]">{t('auth.login.forgot_title')}</strong> {t('auth.login.forgot')}
          </p>
        </div>
      </Card>

      <div className="mt-5 flex flex-wrap justify-center gap-x-3 gap-y-1">
        {!isStandalone() ? (
          <Button variant="ghost" icon={Smartphone} onClick={() => setGuideOpen(true)}>
            {t('auth.login.install_link')}
          </Button>
        ) : null}
        {/* Act 90/2018: the policy must be readable before signing in. */}
        <PrivacyLink />
      </div>

      <Sheet open={guideOpen} onClose={() => setGuideOpen(false)} title={t('auth.install.title')} wide>
        <div className="py-2">
          <InstallGuide />
        </div>
      </Sheet>
    </AuthLayout>
  );
}
